<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import XYZ from "ol/source/XYZ.js";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector.js";
import Feature from "ol/Feature.js";
import Point from "ol/geom/Point.js";
import { fromLonLat } from "ol/proj.js";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style.js";
import Overlay from "ol/Overlay.js";
import Attribution from "ol/control/Attribution.js";
import Zoom from "ol/control/Zoom.js";
import IpGeoPopupCard from "./IpGeoPopupCard.vue";

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
});

const mapEl = ref(null);
const popupEl = ref(null);
const selectedItem = ref(null);

let map = null;
let popup = null;
let markerSource = null;

function createMarkerStyle(label) {
    return new Style({
        image: new CircleStyle({
            radius: 8,
            fill: new Fill({ color: "#1565c0" }),
            stroke: new Stroke({ color: "#fff", width: 2 }),
        }),
        text: new Text({
            text: label,
            offsetY: -20,
            font: "bold 12px sans-serif",
            fill: new Fill({ color: "#1a1a1a" }),
            stroke: new Stroke({ color: "#fff", width: 3 }),
        }),
    });
}

function renderMarkers(items) {
    if (markerSource === null) {
        return;
    }

    markerSource.clear();

    items.forEach((item) => {
        if (!item.geo?.latitude || !item.geo?.longitude) {
            return;
        }

        const feature = new Feature({
            geometry: new Point(
                fromLonLat([
                    Number(item.geo.longitude),
                    Number(item.geo.latitude),
                ]),
            ),
            item,
        });

        feature.setStyle(createMarkerStyle(item.label || item.ip));
        markerSource.addFeature(feature);
    });
}

function closePopup() {
    selectedItem.value = null;
    popup?.setPosition(undefined);
}

function handleClick(event) {
    const feature = map.forEachFeatureAtPixel(
        event.pixel,
        (currentFeature) => currentFeature,
    );

    if (!feature) {
        closePopup();
        return;
    }

    selectedItem.value = feature.get("item");
    popup.setPosition(feature.getGeometry().getCoordinates());
}

function handlePointerMove(event) {
    map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel)
        ? "pointer"
        : "";
}

onMounted(() => {
    markerSource = new VectorSource();

    popup = new Overlay({
        element: popupEl.value,
        positioning: "bottom-center",
        stopEvent: true,
        offset: [0, -14],
        autoPan: true,
    });

    map = new Map({
        target: mapEl.value,
        controls: [
            new Zoom(),
            new Attribution({ collapsible: true, collapsed: true }),
        ],
        layers: [
            new TileLayer({
                source: new XYZ({
                    url: "https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png",
                    attributions: "© OpenStreetMap contributors © CARTO",
                }),
            }),
            new VectorLayer({
                source: markerSource,
            }),
        ],
        overlays: [popup],
        view: new View({
            center: fromLonLat([20, 20]),
            zoom: 2,
        }),
    });

    map.on("click", handleClick);
    map.on("pointermove", handlePointerMove);

    renderMarkers(props.items);
});

watch(
    () => props.items,
    (items) => renderMarkers(items),
    { deep: true },
);

onBeforeUnmount(() => {
    if (map !== null) {
        map.un("click", handleClick);
        map.un("pointermove", handlePointerMove);
        map.setTarget(undefined);
    }
});
</script>

<template>
    <div class="ip-geo-map mb-[22px]">
        <div ref="mapEl" class="ip-geo-map__canvas"></div>

        <div ref="popupEl" class="ip-geo-map__popup">
            <IpGeoPopupCard
                v-if="selectedItem"
                :item="selectedItem"
                @close="closePopup"
            />
        </div>
    </div>
</template>

<style scoped>
.ip-geo-map {
    position: relative;
    width: 100%;
}

.ip-geo-map__canvas {
    width: 100%;
    height: 640px;
    border-radius: 16px;
    overflow: hidden;
}

.ip-geo-map__popup {
    min-width: 260px;
}
</style>
